// Card rows and lists, the empty state, and the Chronicle-style feed.
import type { HTMLAttributes, ReactNode } from 'react';
import { stagger } from './Motion';

export interface CardProps extends HTMLAttributes<HTMLElement> {
  selected?: boolean;
  /** Hover lift + pointer; pass `onClick`. */
  interactive?: boolean;
  column?: boolean;
  dim?: boolean;
  /** Stagger index for the entrance animation. */
  index?: number;
  as?: 'div' | 'article' | 'li' | 'button';
  disabled?: boolean;
  children?: ReactNode;
}

export function Card({ selected, interactive, column, dim, index, as: Tag = 'div', className = '', style, children, disabled, ...rest }: CardProps) {
  const cls = ['fk-card', selected ? 'fk-card--selected' : '', interactive ? 'fk-card--interactive' : '', column ? 'fk-card--column' : '', dim ? 'fk-card--dim' : '', index !== undefined ? 'fk-card--rise' : '', className].filter(Boolean).join(' ');
  const extra = Tag === 'button' ? { type: 'button' as const, disabled } : interactive && rest.onClick ? { tabIndex: 0, role: 'button' } : {};
  return <Tag className={cls} style={{ ...(index !== undefined ? stagger(index) : {}), ...style }} {...extra} {...rest}>{children}</Tag>;
}

export function CardList({ grid, className = '', children, ...rest }: { grid?: boolean; className?: string; children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return <div className={`fk-cards ${grid ? 'fk-cards--grid' : ''} ${className}`} {...rest}>{children}</div>;
}

export function EmptyState({ glyph = '✦', title, children, action, className = '', ...rest }: { glyph?: ReactNode; title?: ReactNode; children?: ReactNode; action?: ReactNode; className?: string } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`fk-empty ${className}`} {...rest}>
      <span className="fk-empty__glyph" aria-hidden="true">{glyph}</span>
      {title && <div className="fk-empty__title">{title}</div>}
      {children && <div className="fk-empty__text">{children}</div>}
      {action}
    </div>
  );
}

/** Timestamped lines (Chronicle, party log). Each row animates in. */
export function Feed({ items, className = '', ...rest }: { items: ReadonlyArray<{ key: string; time?: string; text: ReactNode; glyph?: ReactNode; glyphTitle?: string }>; className?: string } & HTMLAttributes<HTMLUListElement>) {
  return (
    <ul className={`fk-feed ${className}`} {...rest}>
      {items.map((i) => <li key={i.key}>{i.time && <time>{i.time}</time>}<span>{i.text}</span>{i.glyph && <span className="fk-glyph" title={i.glyphTitle} aria-label={i.glyphTitle}>{i.glyph}</span>}</li>)}
    </ul>
  );
}
